import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const setAuthHeader = useCallback((tkn) => {
    if (tkn) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${tkn}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        setAuthHeader(storedToken);
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          console.error("Auth init failed:", error);
          localStorage.removeItem("token");
          setToken(null);
          setAuthHeader(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [setAuthHeader]);

  // Intercept 401 responses — auto logout on expired token
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
          setAuthHeader(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, setAuthHeader]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    setAuthHeader(newToken);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    setAuthHeader(newToken);
    try { await axios.post(`${API_URL}/seed-data`); } catch (e) {}
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setAuthHeader(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    const perms = user.permissions || [];
    if (perms.includes("*")) return true;
    return perms.includes(permission);
  };

  const canRead = (resource) => hasPermission(`${resource}:read`);
  const canWrite = (resource) => hasPermission(`${resource}:write`);
  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      isAuthenticated: !!token && !!user,
      login, register, logout,
      hasPermission, canRead, canWrite, isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
